import React, { useRef, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Upload, X, Loader2, FileUp, MapPin, PawPrint, Droplets, CheckCircle2,
  Mountain, Activity, Users, Flag, Tent, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// Sage-green pin — matches the campsite marker style used elsewhere so the
// operator immediately sees "this is where the campsite will sit".
const pickerIcon = L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="td-marker" style="background:#3F5D44;color:#F6EFE0;">⛺</div>`,
});

// Distinct start/finish pins for the GPX trail picker — sage for the
// start flag (matches brand primary, "go") and terracotta for the finish
// (matches basecamp accent, "arrival"). Reuses the same divIcon shape as
// the campsite picker so the visual language stays consistent.
const startIcon = L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="td-marker" style="background:#3F5D44;color:#F6EFE0;">🏁</div>`,
});
const finishIcon = L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="td-marker" style="background:#B16B3C;color:#F6EFE0;">🎯</div>`,
});

/** Inner map child that captures single-clicks and reports lat/lng back up
 *  the tree. Kept as its own component because `useMapEvents` has to live
 *  inside the <MapContainer> subtree. */
function ClickToSetCoords({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

const RATINGS = [
  { value: "accessible", label: "Dog-friendly route" },
  { value: "difficult", label: "Stiles on route" },
  { value: "restricted", label: "Dogs not permitted" },
  { value: "unknown", label: "Unknown — set later" },
];

/**
 * Client-side GPX peek so we can pre-fill name / desc / point-count AND
 * the exact first and last coordinates BEFORE the user submits, without a
 * second backend round-trip. The start/finish here mirror the backend
 * resolution priority so what the admin sees on the picker map matches
 * what the server would compute if left untouched.
 */
function peekGpx(text) {
  try {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    if (doc.querySelector("parsererror")) return null;
    const name = doc.querySelector("trk > name, rte > name, metadata > name, name")?.textContent?.trim() || "";
    const desc = doc.querySelector("trk > desc, rte > desc, metadata > desc, desc")?.textContent?.trim() || "";
    const pts = doc.querySelectorAll("trkpt, rtept");
    const wpts = doc.querySelectorAll("wpt");
    const readCoord = (el) => {
      const lat = parseFloat(el.getAttribute("lat"));
      const lng = parseFloat(el.getAttribute("lon"));
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    };
    // First waypoint wins for the start when present, otherwise the very
    // first track/route point. Finish is trkpt/rtept[last] with a wpt[last]
    // fallback when the file is waypoint-only.
    let start = null;
    let finish = null;
    if (wpts.length > 0) start = readCoord(wpts[0]);
    if (!start && pts.length > 0) start = readCoord(pts[0]);
    if (pts.length > 0) finish = readCoord(pts[pts.length - 1]);
    if (!finish && wpts.length > 0) finish = readCoord(wpts[wpts.length - 1]);
    if (!finish) finish = start; // single-point degenerate case
    return { name, desc, point_count: pts.length, start, finish };
  } catch {
    return null;
  }
}

export default function AdminAddTrail() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [locationType, setLocationType] = useState("trail"); // 'trail' | 'campsite'
  const [form, setForm] = useState({
    name: "",
    region: "",
    short_desc: "",
    dog_friendly_rating: "unknown",
    water_source_available: false,
    ozzy_verified: true, // sensible default for admin-curated locations
    editor_pick: false,
    // Trail start / finish overrides. Populated automatically on GPX peek,
    // editable via the click-to-drop picker below. Empty strings mean
    // "use whatever the backend parses from the file".
    start_latitude: "",
    start_longitude: "",
    finish_latitude: "",
    finish_longitude: "",
    // Campsite-only:
    address: "",
    google_maps_url: "",
    point_latitude: "",
    point_longitude: "",
  });
  // Which flag does the next map-click drop — start or finish? Reuses the
  // exact click-to-drop pattern from the basecamps / campsites picker; the
  // only new UI is the mode toggle above the map.
  const [pinMode, setPinMode] = useState("start");

  const trailMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name);
      fd.append("region", form.region);
      fd.append("short_desc", form.short_desc);
      fd.append("dog_friendly_rating", form.dog_friendly_rating);
      fd.append("water_source_available", String(form.water_source_available));
      fd.append("ozzy_verified", String(form.ozzy_verified));
      fd.append("editor_pick", String(form.editor_pick));
      // Manual overrides — only appended when the admin has actually
      // touched them. Backend precedence is manual > gpx, so leaving them
      // blank means "trust whatever's in the GPX".
      if (form.start_latitude && form.start_longitude) {
        fd.append("start_lat", form.start_latitude);
        fd.append("start_lng", form.start_longitude);
      }
      if (form.finish_latitude && form.finish_longitude) {
        fd.append("finish_lat", form.finish_latitude);
        fd.append("finish_lng", form.finish_longitude);
      }
      return (await api.post("/admin/trails/import-gpx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: (data) => {
      toast.success(`Trail "${data.name}" created with ${data.point_count} points`);
      navigate(`/location/${data.trail_id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't import this GPX"),
  });

  const campsiteMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        region: form.region || undefined,
        short_desc: form.short_desc || undefined,
        address: form.address || undefined,
        google_maps_url: form.google_maps_url || undefined,
        dog_friendly_rating: form.dog_friendly_rating,
        water_source_available: form.water_source_available,
        ozzy_verified: form.ozzy_verified,
        editor_pick: form.editor_pick,
      };
      if (form.point_latitude !== "" && form.point_longitude !== "") {
        payload.point_latitude = parseFloat(form.point_latitude);
        payload.point_longitude = parseFloat(form.point_longitude);
      }
      return (await api.post("/admin/locations/campsite", payload)).data;
    },
    onSuccess: (data) => {
      toast.success(`Campsite "${data.name}" created`);
      navigate(`/location/${data.location_id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't create campsite"),
  });

  const mut = locationType === "trail" ? trailMut : campsiteMut;

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".gpx")) {
      toast.error("Please pick a .gpx file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setFile(f);
    const text = await f.text();
    const meta = peekGpx(text);
    setPreview(meta);
    if (meta) {
      setForm((s) => ({
        ...s,
        name: s.name || meta.name || f.name.replace(/\.gpx$/i, ""),
        short_desc: s.short_desc || meta.desc || "",
        // Pre-populate the manual override fields from the parsed start
        // and finish so the picker map has an initial pin at the right
        // place — admin can then drag or click to reposition.
        start_latitude: meta.start ? meta.start.lat.toFixed(6) : s.start_latitude,
        start_longitude: meta.start ? meta.start.lng.toFixed(6) : s.start_longitude,
        finish_latitude: meta.finish ? meta.finish.lat.toFixed(6) : s.finish_latitude,
        finish_longitude: meta.finish ? meta.finish.lng.toFixed(6) : s.finish_longitude,
      }));
      setPinMode("start");
    }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-add-trail-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="td-section-label">Admin</div>
          <h1 className="font-heading text-4xl font-semibold text-brand-text mt-1 flex items-center gap-3">
            <FileUp size={28} className="text-brand-primary" /> Add {locationType === "trail" ? "trail" : "campsite"}
          </h1>
          <p className="text-brand-subtle text-sm mt-1">
            {locationType === "trail"
              ? "Upload a single .gpx file. Name & description auto-fill from the file — override anything before saving."
              : "Paste a Google Maps URL (or enter manual coordinates) — we'll pin the campsite to the map."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/telemetry" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Activity size={14} /> Telemetry
          </Link>
          <Link to="/admin/users" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Users size={14} /> Users
          </Link>
          <Link to="/admin/reports" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Flag size={14} /> Reports
          </Link>
        </div>
      </header>

      {/* Location type toggle */}
      <div className="td-card p-4 mb-4" data-testid="location-type-toggle-card">
        <div className="td-section-label mb-2">What are you adding?</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="loc-type-trail"
            type="button"
            onClick={() => setLocationType("trail")}
            className={`flex items-center justify-center gap-2 py-3 rounded-2xl border text-sm font-medium transition-colors ${
              locationType === "trail"
                ? "bg-brand-primary text-brand-sand border-brand-primary"
                : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-primary/40"
            }`}
          >
            <Mountain size={16} /> Trail (GPX)
          </button>
          <button
            data-testid="loc-type-campsite"
            type="button"
            onClick={() => setLocationType("campsite")}
            className={`flex items-center justify-center gap-2 py-3 rounded-2xl border text-sm font-medium transition-colors ${
              locationType === "campsite"
                ? "bg-brand-primary text-brand-sand border-brand-primary"
                : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-primary/40"
            }`}
          >
            <Tent size={16} /> Campsite (pin)
          </button>
        </div>
      </div>

      {/* File picker — trail only */}
      {locationType === "trail" && (
      <div className="td-card p-5 mb-4" data-testid="trail-file-card">
        <div className="td-section-label">1. GPX file</div>
        {file ? (
          <div className="mt-2 flex items-center gap-3 p-3 rounded-2xl border border-brand-border bg-brand-sand/60">
            <Mountain size={20} className="text-brand-primary" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-brand-text truncate">{file.name}</div>
              <div className="text-xs text-brand-subtle">
                {(file.size / 1024).toFixed(1)} KB
                {preview && ` · ${preview.point_count} track points`}
              </div>
            </div>
            <button
              data-testid="add-trail-clear-file"
              onClick={() => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
              className="text-brand-subtle"
              aria-label="Remove"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <label
            htmlFor="gpx-input"
            className="mt-2 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-brand-border rounded-2xl py-10 text-sm text-brand-subtle cursor-pointer hover:bg-brand-sand transition-colors"
          >
            <Upload size={24} />
            <span>Tap to choose a .gpx file (max 5 MB)</span>
          </label>
        )}
        <input
          ref={fileRef}
          id="gpx-input"
          data-testid="add-trail-file-input"
          type="file"
          accept=".gpx,application/gpx+xml,application/xml,text/xml"
          onChange={onPick}
          className="hidden"
        />
      </div>
      )}

      {/* Trail start / finish picker — reuses the exact click-to-drop
          pattern from the basecamp/campsite picker (ClickToSetCoords +
          divIcon marker). A segmented mode toggle decides whether the
          next click drops the start flag or the finish flag; both pins
          render simultaneously so the operator can eyeball the whole
          route before saving.

          Render triggers (any one is enough):
            • a fresh GPX file was just picked (add-new flow), OR
            • start OR finish coordinates are already populated in state —
              e.g. an existing trail loaded from the database into
              form.start_latitude / form.finish_latitude, an edit-mode
              parent pre-filled the card, or the peek retained coords
              from a previous file selection.
          This keeps the add-new flow untouched while unlocking the same
          picker for edit-mode use without any duplication. */}
      {locationType === "trail" && (
        file
        || form.start_latitude
        || form.start_longitude
        || form.finish_latitude
        || form.finish_longitude
      ) && (
      <div className="td-card p-5 mb-4" data-testid="trail-endpoints-card">
        <div className="td-section-label">1b. Start &amp; finish</div>
        <p className="text-xs text-brand-subtle mt-1">
          {file
            ? <>Auto-filled from the GPX ({preview?.point_count || 0} track points). Click the map to override — a single mistagged first point is a two-click fix.</>
            : <>Loaded from the saved trail. Click the map to reposition either flag — the mode toggle below decides which one moves.</>
          }
        </p>

        <div
          className="mt-3 grid grid-cols-2 gap-2"
          data-testid="trail-pin-mode-toggle"
        >
          <button
            type="button"
            data-testid="trail-pin-mode-start"
            onClick={() => setPinMode("start")}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-sm font-medium transition-colors ${
              pinMode === "start"
                ? "bg-brand-primary text-brand-sand border-brand-primary"
                : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-primary/40"
            }`}
          >
            🏁 Setting start
          </button>
          <button
            type="button"
            data-testid="trail-pin-mode-finish"
            onClick={() => setPinMode("finish")}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-sm font-medium transition-colors ${
              pinMode === "finish"
                ? "bg-brand-accent text-brand-sand border-brand-accent"
                : "bg-brand-surface text-brand-text border-brand-border hover:border-brand-accent/40"
            }`}
          >
            🎯 Setting finish
          </button>
        </div>

        <div
          data-testid="trail-endpoints-map"
          className="td-card overflow-hidden h-64 mt-3"
        >
          <MapContainer
            // Prefer the saved start; fall back to saved finish; only
            // then reach for the Snowdonia default. This keeps edit-mode
            // maps zoomed on the trail's real location instead of
            // dropping the operator in North Wales every time.
            center={[
              form.start_latitude
                ? parseFloat(form.start_latitude)
                : form.finish_latitude
                  ? parseFloat(form.finish_latitude)
                  : 53.07,
              form.start_longitude
                ? parseFloat(form.start_longitude)
                : form.finish_longitude
                  ? parseFloat(form.finish_longitude)
                  : -4.08,
            ]}
            zoom={13}
            scrollWheelZoom={false}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
            />
            <ClickToSetCoords
              onPick={(lat, lng) => {
                // Writes into the active pin's coord fields — exact same
                // downstream flow as the basecamp picker, just with a
                // conditional target based on the mode toggle above.
                setForm((s) => (
                  pinMode === "start"
                    ? { ...s, start_latitude: lat.toFixed(6), start_longitude: lng.toFixed(6) }
                    : { ...s, finish_latitude: lat.toFixed(6), finish_longitude: lng.toFixed(6) }
                ));
              }}
            />
            {form.start_latitude && form.start_longitude
              && !isNaN(parseFloat(form.start_latitude))
              && !isNaN(parseFloat(form.start_longitude)) && (
              <Marker
                position={[parseFloat(form.start_latitude), parseFloat(form.start_longitude)]}
                icon={startIcon}
              />
            )}
            {form.finish_latitude && form.finish_longitude
              && !isNaN(parseFloat(form.finish_latitude))
              && !isNaN(parseFloat(form.finish_longitude)) && (
              <Marker
                position={[parseFloat(form.finish_latitude), parseFloat(form.finish_longitude)]}
                icon={finishIcon}
              />
            )}
          </MapContainer>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="p-3 rounded-2xl bg-brand-primary/5 border border-brand-primary/30">
            <div className="td-section-label text-brand-primary">🏁 Start</div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <input
                data-testid="trail-start-lat"
                type="number"
                step="0.000001"
                value={form.start_latitude}
                onChange={(e) => set("start_latitude", e.target.value)}
                placeholder="Lat"
                className="td-input !text-xs"
              />
              <input
                data-testid="trail-start-lng"
                type="number"
                step="0.000001"
                value={form.start_longitude}
                onChange={(e) => set("start_longitude", e.target.value)}
                placeholder="Lng"
                className="td-input !text-xs"
              />
            </div>
          </div>
          <div className="p-3 rounded-2xl bg-brand-accent/5 border border-brand-accent/30">
            <div className="td-section-label text-brand-accent">🎯 Finish</div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <input
                data-testid="trail-finish-lat"
                type="number"
                step="0.000001"
                value={form.finish_latitude}
                onChange={(e) => set("finish_latitude", e.target.value)}
                placeholder="Lat"
                className="td-input !text-xs"
              />
              <input
                data-testid="trail-finish-lng"
                type="number"
                step="0.000001"
                value={form.finish_longitude}
                onChange={(e) => set("finish_longitude", e.target.value)}
                placeholder="Lng"
                className="td-input !text-xs"
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Campsite location card — campsite only */}
      {locationType === "campsite" && (
      <div className="td-card p-5 mb-4" data-testid="campsite-location-card">
        <div className="td-section-label">1. Campsite location</div>

        <label className="block text-sm font-medium text-brand-text mt-3">Address (optional)</label>
        <input
          data-testid="campsite-address"
          value={form.address}
          onChange={(e) => set("address", e.target.value)}
          placeholder="e.g. The Old Barn, Lower Beeding, West Sussex"
          maxLength={300}
          className="td-input mt-1"
        />

        <label className="block text-sm font-medium text-brand-text mt-3 flex items-center gap-1.5">
          <LinkIcon size={13} className="text-brand-subtle" /> Google Maps URL
        </label>
        <input
          data-testid="campsite-gmaps-url"
          value={form.google_maps_url}
          onChange={(e) => set("google_maps_url", e.target.value)}
          placeholder="https://www.google.com/maps/place/..."
          maxLength={600}
          className="td-input mt-1"
        />
        <p className="text-xs text-brand-subtle mt-1">
          We'll auto-extract latitude &amp; longitude from the URL. Paste the expanded link — short links (maps.app.goo.gl) won't be resolved server-side.
        </p>

        {/* Interactive picker map — click anywhere to auto-fill the manual
            coord inputs below. Same default centre + tile as the basecamp
            picker so operators build muscle memory across forms. */}
        <div className="mt-4">
          <div className="td-section-label flex items-center gap-1">
            <MapPin size={11} /> Pick on the map
            <span className="text-brand-subtle font-normal">(click to drop the campsite)</span>
          </div>
          <div
            data-testid="campsite-map-picker"
            className="td-card overflow-hidden h-56 mt-1"
          >
            <MapContainer
              center={[51.58, -2.99]}
              zoom={13}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
              <ClickToSetCoords
                onPick={(lat, lng) => {
                  // Writes into the existing manual-override state — same
                  // fields the campsite create mutation already reads, so
                  // downstream validation / submit are untouched.
                  setForm((s) => ({
                    ...s,
                    point_latitude: lat.toFixed(6),
                    point_longitude: lng.toFixed(6),
                  }));
                }}
              />
              {form.point_latitude && form.point_longitude
                && !isNaN(parseFloat(form.point_latitude))
                && !isNaN(parseFloat(form.point_longitude)) && (
                <Marker
                  position={[parseFloat(form.point_latitude), parseFloat(form.point_longitude)]}
                  icon={pickerIcon}
                />
              )}
            </MapContainer>
          </div>
          <p className="text-[11px] text-brand-subtle mt-1">
            Tip: paste a Google Maps URL above for a precise drop, or click the map here for a quick pin.
          </p>
        </div>

        <div className="mt-4 p-3 rounded-2xl bg-brand-sand/50 border border-brand-border/60">
          <div className="td-section-label">Manual Coordinates Override</div>
          <p className="text-xs text-brand-subtle mt-1">
            Fill these in to override whatever's extracted from the URL.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="block text-xs font-medium text-brand-subtle">Latitude</label>
              <input
                data-testid="campsite-lat"
                type="number"
                step="0.0001"
                value={form.point_latitude}
                onChange={(e) => set("point_latitude", e.target.value)}
                placeholder="51.2519"
                className="td-input mt-1 !text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-subtle">Longitude</label>
              <input
                data-testid="campsite-lng"
                type="number"
                step="0.0001"
                value={form.point_longitude}
                onChange={(e) => set("point_longitude", e.target.value)}
                placeholder="-0.3127"
                className="td-input mt-1 !text-sm"
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Metadata form */}
      <fieldset
        disabled={locationType === "trail" && !file}
        className={`td-card p-5 transition-opacity ${locationType === "trail" && !file ? "opacity-50" : ""}`}
      >
        <div className="td-section-label">2. {locationType === "trail" ? "Trail" : "Campsite"} details</div>

        <label className="block text-sm font-medium text-brand-text mt-3">{locationType === "trail" ? "Trail" : "Campsite"} name</label>
        <input
          data-testid="add-trail-name"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder={preview?.name || (locationType === "trail" ? "Auto-filled from GPX" : "e.g. The Old Barn Camping")}
          className="td-input mt-1"
        />

        <label className="block text-sm font-medium text-brand-text mt-3">Region</label>
        <input
          data-testid="add-trail-region"
          value={form.region}
          onChange={(e) => set("region", e.target.value)}
          placeholder="e.g. Surrey · Snowdonia · Lake District"
          className="td-input mt-1"
        />

        <label className="block text-sm font-medium text-brand-text mt-3">Short description</label>
        <textarea
          data-testid="add-trail-desc"
          value={form.short_desc}
          onChange={(e) => set("short_desc", e.target.value)}
          rows={3}
          maxLength={500}
          placeholder={preview?.desc || "Auto-filled from GPX <desc> if present"}
          className="td-input mt-1 resize-none"
        />

        <label className="block text-sm font-medium text-brand-text mt-3">Dog-friendliness</label>
        <select
          data-testid="add-trail-rating"
          value={form.dog_friendly_rating}
          onChange={(e) => set("dog_friendly_rating", e.target.value)}
          className="td-input mt-1"
        >
          {RATINGS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>

        {/* Toggles */}
        <label className="mt-4 flex items-start gap-3 p-3 rounded-2xl border border-brand-border cursor-pointer hover:border-brand-primary/40">
          <input
            data-testid="add-trail-ozzy"
            type="checkbox"
            checked={form.ozzy_verified}
            onChange={(e) => set("ozzy_verified", e.target.checked)}
            className="mt-1 w-4 h-4 accent-brand-accent"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-brand-text">
              <PawPrint size={14} className="text-brand-accent" /> Ozzy Verified
            </span>
            <span className="block text-xs text-brand-subtle mt-0.5">
              Mark this trail as personally walked and approved — a special badge will appear everywhere it's listed.
            </span>
          </span>
        </label>

        <label className="mt-3 flex items-start gap-3 p-3 rounded-2xl border border-brand-border cursor-pointer hover:border-brand-primary/40">
          <input
            data-testid="add-trail-water"
            type="checkbox"
            checked={form.water_source_available}
            onChange={(e) => set("water_source_available", e.target.checked)}
            className="mt-1 w-4 h-4 accent-brand-primary"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-brand-text">
              <Droplets size={14} className="text-[#2E7AB8]" /> Water source available
            </span>
            <span className="block text-xs text-brand-subtle mt-0.5">
              Tick if there&apos;s a stream, tap, or natural water along the route.
            </span>
          </span>
        </label>

        <label className="mt-3 flex items-center gap-3 px-3 py-2 cursor-pointer">
          <input
            data-testid="add-trail-editor"
            type="checkbox"
            checked={form.editor_pick}
            onChange={(e) => set("editor_pick", e.target.checked)}
            className="w-4 h-4 accent-brand-accent"
          />
          <span className="text-sm text-brand-text">Editor&apos;s pick</span>
        </label>

        <button
          data-testid="add-trail-submit"
          type="button"
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !form.name.trim() || (locationType === "trail" && !file) || (locationType === "campsite" && !form.google_maps_url && (form.point_latitude === "" || form.point_longitude === ""))}
          className="td-btn-primary w-full mt-5 disabled:opacity-50"
        >
          {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Create {locationType === "trail" ? "trail" : "campsite"}
        </button>

        {locationType === "trail" && preview && (
          <p className="text-xs text-brand-subtle mt-3 flex items-center gap-1">
            <MapPin size={12} /> {preview.point_count} track points will be saved as the route geometry.
          </p>
        )}
        {locationType === "campsite" && (
          <p className="text-xs text-brand-subtle mt-3 flex items-center gap-1">
            <MapPin size={12} /> Single pin marker — manual coords take priority over Google Maps URL.
          </p>
        )}
      </fieldset>
    </div>
  );
}
